import React, { useEffect, useState } from 'react';
import projectAPI from '../api/projectAPI';
import AdminNavbar from './AdminNavbar';
import {
  FaEdit, FaTrash, FaChevronRight, FaTimes,
  FaCheckCircle, FaFileAlt, FaDownload, FaEye,
  FaFilePdf, FaFileWord, FaFileImage, FaFile,
} from 'react-icons/fa';
import { MdEdit } from 'react-icons/md';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// ── Helpers ───────────────────────────────────────────────────────────────────
const ROLE_COLOR = {
  'Marketing':     'bg-pink-100 text-pink-700',
  'Project Leads': 'bg-indigo-100 text-indigo-700',
  'HR & Design':   'bg-teal-100 text-teal-700',
  'TL':            'bg-blue-100 text-blue-700',
  'Developer':     'bg-green-100 text-green-700',
  'Office':        'bg-gray-100 text-gray-600',
  'admin':         'bg-purple-100 text-purple-700',
};
const RoleBadge = ({ role }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLOR[role] || 'bg-gray-100 text-gray-600'}`}>
    {role}
  </span>
);

const StatusBadge = ({ status }) => {
  const s =
    status === 'Completed' ? 'bg-green-100 text-green-700' :
    status === 'On Hold'   ? 'bg-blue-100 text-blue-700'   :
                             'bg-yellow-100 text-yellow-700';
  return <span className={`text-xs font-semibold px-2 py-1 rounded-full ${s}`}>{status}</span>;
};

const ChainTrail = ({ chain }) => {
  if (!chain?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {chain.map((entry, i) => (
        <React.Fragment key={i}>
          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full">
            {entry}
          </span>
          {i < chain.length - 1 && <FaChevronRight className="text-purple-300 text-xs" />}
        </React.Fragment>
      ))}
    </div>
  );
};

const FileIcon = ({ fileType }) => {
  if (fileType === 'image') return <FaFileImage className="text-blue-500 text-lg shrink-0" />;
  if (fileType === 'pdf')   return <FaFilePdf   className="text-red-500  text-lg shrink-0" />;
  if (fileType === 'doc')   return <FaFileWord  className="text-blue-700 text-lg shrink-0" />;
  return <FaFile className="text-gray-500 text-lg shrink-0" />;
};

const fmtDate = (d) => {
  if (!d) return '—';
  const p = new Date(d);
  return isNaN(p) ? '—' : p.toLocaleDateString('en-GB');
};

// ─────────────────────────────────────────────────────────────────────────────
const AdminProjectManager = () => {
  const [projects, setProjects]         = useState([]);
  const [searchTerm, setSearchTerm]     = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showChart, setShowChart]       = useState(false);

  // ── Inline status edit ────────────────────────────────────────────────────
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [newStatus, setNewStatus]             = useState('');
  const [savingStatus, setSavingStatus]       = useState(false);

  // ── Detail / Reports modal ────────────────────────────────────────────────
  const [showDetail, setShowDetail]       = useState(false);
  const [detailProject, setDetailProject] = useState(null);
  const [previewUrl, setPreviewUrl]       = useState(null);

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const res = await projectAPI.get('/');
      setProjects(res.data);
    } catch (err) { console.error('Error fetching projects:', err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try { await projectAPI.delete(`/${id}`); fetchProjects(); }
    catch (err) { console.error('Error deleting project:', err); }
  };

  // ── Status update (admin only) ────────────────────────────────────────────
  const saveStatus = async (projectId) => {
    setSavingStatus(true);
    try {
      await projectAPI.put(`/${projectId}`, { status: newStatus });
      setProjects((prev) =>
        prev.map((p) => p._id === projectId ? { ...p, status: newStatus } : p)
      );
      if (detailProject?._id === projectId) {
        setDetailProject((prev) => ({ ...prev, status: newStatus }));
      }
      setEditingStatusId(null);
      alert('✅ Status updated!');
    } catch (err) {
      alert('❌ Failed to update status.');
      console.error(err);
    } finally { setSavingStatus(false); }
  };

  // ── Chart data ────────────────────────────────────────────────────────────
  const chartData = [
    { status: 'Ongoing',   count: projects.filter(p => p.status === 'Ongoing').length },
    { status: 'Completed', count: projects.filter(p => p.status === 'Completed').length },
    { status: 'On Hold',   count: projects.filter(p => p.status === 'On Hold').length },
  ];

  // ── Filtered rows ─────────────────────────────────────────────────────────
  const filtered = projects.filter((p) => {
    const s = searchTerm.toLowerCase();
    const matchSearch =
      p.title?.toLowerCase().includes(s) ||
      p.assignees?.some((a) => a.username?.toLowerCase().includes(s)) ||
      p.createdBy?.username?.toLowerCase().includes(s);
    const matchStatus = statusFilter === 'All' || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div className="min-h-screen bg-purple-100 p-6 pt-20">
      <AdminNavbar />

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center mb-5 mt-4 gap-3">
        <h1 className="text-3xl font-bold font-mono text-purple-700">Project Tracking</h1>
        <div className="flex gap-2 flex-wrap items-center">
          {['All', 'Ongoing', 'On Hold', 'Completed'].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                statusFilter === s
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'
              }`}>{s}</button>
          ))}
          <button onClick={() => setShowChart(!showChart)}
            className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 text-sm">
            {showChart ? 'Back to Projects' : 'View Chart'}
          </button>
        </div>
      </div>

      {showChart ? (
        <div className="bg-white p-6 rounded-xl shadow max-w-4xl mx-auto mt-6">
          <h2 className="text-xl font-semibold text-purple-600 mb-4">Project Status Chart</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <XAxis dataKey="status" /><YAxis allowDecimals={false} />
              <Tooltip /><Legend />
              <Bar dataKey="count" fill="#9F7AEA" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="bg-white p-4 rounded-xl shadow overflow-x-auto max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
            <h3 className="text-xl font-semibold text-purple-600">
              All Projects <span className="text-sm text-gray-400 font-normal ml-1">({filtered.length})</span>
            </h3>
            <input
              type="text"
              placeholder="Search by title, assignee or creator…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border px-4 py-2 rounded-lg w-full md:w-80 text-sm"
            />
          </div>

          <div className="overflow-y-auto max-h-[500px]">
            <table className="min-w-full border text-left text-sm">
              <thead className="bg-purple-100 text-purple-700 sticky top-0 z-10">
                <tr>
                  <th className="p-2">Title</th>
                  <th className="p-2">Created By</th>
                  <th className="p-2">Assigned To</th>
                  <th className="p-2">Chain</th>
                  <th className="p-2">Start</th>
                  <th className="p-2">End</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Files</th>
                  <th className="p-2">Reports</th>
                  <th className="p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan="10" className="text-center py-8 text-gray-400">No projects found.</td></tr>
                )}
                {filtered.map((project) => (
                  <tr key={project._id} className="border-t hover:bg-purple-50 transition">

                    {/* Title */}
                    <td className="p-2">
                      <p className="font-semibold text-gray-800">{project.title}</p>
                      {project.description && (
                        <p className="text-xs text-gray-400 line-clamp-1">{project.description}</p>
                      )}
                    </td>

                    {/* Created by */}
                    <td className="p-2">
                      {project.createdBy?.username ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="text-xs text-gray-700 font-medium">{project.createdBy.username}</span>
                          <RoleBadge role={project.createdBy.role} />
                        </div>
                      ) : <span className="text-xs text-gray-400">—</span>}
                    </td>

                    {/* Assigned to */}
                    <td className="p-2">
                      {project.assignees?.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {project.assignees.slice(0, 2).map((a, i) => (
                            <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                              {a.username}
                            </span>
                          ))}
                          {project.assignees.length > 2 && (
                            <span className="text-xs text-gray-400">+{project.assignees.length - 2}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {project.assignedTo?.map(u => typeof u === 'object' ? u.username : '?').join(', ') || '—'}
                        </span>
                      )}
                    </td>

                    {/* Chain */}
                    <td className="p-2">
                      {project.assignmentChain?.length > 0
                        ? <ChainTrail chain={project.assignmentChain} />
                        : <span className="text-xs text-gray-400">—</span>}
                    </td>

                    {/* Dates */}
                    <td className="p-2 whitespace-nowrap text-xs">{fmtDate(project.startDate)}</td>
                    <td className="p-2 whitespace-nowrap text-xs">{fmtDate(project.endDate)}</td>

                    {/* Status — admin can edit inline */}
                    <td className="p-2">
                      {editingStatusId === project._id ? (
                        <div className="flex items-center gap-1">
                          <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                            className="border rounded px-2 py-1 text-xs focus:ring-2 focus:ring-purple-400 outline-none">
                            <option value="Ongoing">Ongoing</option>
                            <option value="On Hold">On Hold</option>
                            <option value="Completed">Completed</option>
                          </select>
                          <button onClick={() => saveStatus(project._id)} disabled={savingStatus}
                            className="text-green-600 hover:text-green-800 disabled:opacity-50">
                            <FaCheckCircle />
                          </button>
                          <button onClick={() => setEditingStatusId(null)} className="text-red-400 hover:text-red-600">
                            <FaTimes />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <StatusBadge status={project.status} />
                          <button
                            onClick={() => { setEditingStatusId(project._id); setNewStatus(project.status); }}
                            className="text-gray-400 hover:text-purple-600 text-sm" title="Edit status">
                            <MdEdit />
                          </button>
                        </div>
                      )}
                    </td>

                    {/* Files */}
                    <td className="p-2 text-xs text-blue-600">
                      {project.attachments?.length > 0 ? `${project.attachments.length} file(s)` : '—'}
                    </td>

                    {/* Reports */}
                    <td className="p-2 text-xs text-indigo-600">
                      {project.reports?.length > 0 ? `${project.reports.length} report(s)` : '—'}
                    </td>

                    {/* Actions */}
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setDetailProject(project); setShowDetail(true); }}
                          className="flex items-center gap-1 text-xs bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded-lg">
                          <FaFileAlt /> View
                        </button>
                        <button onClick={() => handleDelete(project._id)}
                          className="text-red-500 hover:text-red-700 text-sm">
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── DETAIL MODAL ─────────────────────────────────────────────────────── */}
      {showDetail && detailProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-2xl rounded-xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowDetail(false)}
              className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>

            {/* Title + status */}
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
              <div>
                <h3 className="text-xl font-bold text-purple-700">{detailProject.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{detailProject.description || '—'}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={detailProject.status} />
                {/* Admin status edit inside modal */}
                {editingStatusId === detailProject._id ? (
                  <div className="flex items-center gap-1">
                    <select value={newStatus} onChange={e => setNewStatus(e.target.value)}
                      className="border rounded px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-purple-400">
                      <option value="Ongoing">Ongoing</option>
                      <option value="On Hold">On Hold</option>
                      <option value="Completed">Completed</option>
                    </select>
                    <button onClick={() => saveStatus(detailProject._id)} disabled={savingStatus}
                      className="text-green-600 hover:text-green-800 disabled:opacity-50"><FaCheckCircle /></button>
                    <button onClick={() => setEditingStatusId(null)} className="text-red-400 hover:text-red-600"><FaTimes /></button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setEditingStatusId(detailProject._id); setNewStatus(detailProject.status); }}
                    className="text-xs flex items-center gap-1 text-purple-600 hover:text-purple-800 border border-purple-200 px-2 py-0.5 rounded-lg">
                    <MdEdit /> Edit Status
                  </button>
                )}
              </div>
            </div>

            {/* Dates */}
            <div className="flex gap-6 text-xs text-gray-500 mb-4">
              <span>Start: <span className="font-medium text-gray-700">{fmtDate(detailProject.startDate)}</span></span>
              <span>End: <span className="font-medium text-gray-700">{fmtDate(detailProject.endDate)}</span></span>
            </div>

            {/* Created by */}
            {detailProject.createdBy?.username && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Created by</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-700">{detailProject.createdBy.username}</span>
                  <RoleBadge role={detailProject.createdBy.role} />
                </div>
              </div>
            )}

            {/* Chain */}
            {detailProject.assignmentChain?.length > 0 && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-2">Assignment chain</p>
                <ChainTrail chain={detailProject.assignmentChain} />
              </div>
            )}

            {/* Assignees */}
            {detailProject.assignees?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-400 mb-2">All assignees</p>
                <div className="space-y-2">
                  {detailProject.assignees.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg border bg-gray-50">
                      <span className="text-sm font-medium text-gray-700 flex-1">{a.username}</span>
                      <RoleBadge role={a.role} />
                      <span className="text-xs text-gray-400">← {a.assignedBy}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Attachments */}
            {detailProject.attachments?.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-400 mb-2 uppercase font-medium tracking-wide">
                  Attachments ({detailProject.attachments.length})
                </p>
                <div className="space-y-2">
                  {detailProject.attachments.map((f) => (
                    <div key={f._id} className="flex items-center gap-3 bg-gray-50 border rounded-lg px-3 py-2">
                      <FileIcon fileType={f.fileType} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{f.name}</p>
                        <p className="text-xs text-gray-400">by {f.uploadedBy} · {new Date(f.uploadedAt).toLocaleDateString('en-GB')}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {f.fileType === 'image' && (
                          <button onClick={() => setPreviewUrl(f.url)} className="text-blue-500 hover:text-blue-700"><FaEye /></button>
                        )}
                        <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-700"><FaDownload /></a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reports */}
            {detailProject.reports?.length > 0 && (
              <div className="mb-2">
                <p className="text-xs text-gray-400 mb-2 uppercase font-medium tracking-wide">
                  Reports ({detailProject.reports.length})
                </p>
                <div className="space-y-3">
                  {detailProject.reports.map((r, i) => (
                    <div key={i} className="border rounded-xl p-3 bg-indigo-50">
                      <div className="flex justify-between items-start flex-wrap gap-1 mb-2">
                        <span className="text-sm font-semibold text-indigo-700">{r.submittedBy}</span>
                        <span className="text-xs text-gray-400">{new Date(r.submittedAt).toLocaleString('en-GB')}</span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">{r.description}</p>
                      {r.photo && (
                        <img src={r.photo} alt="report"
                          className="w-full rounded-lg border cursor-pointer hover:opacity-90"
                          onClick={() => setPreviewUrl(r.photo)} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!detailProject.attachments?.length && !detailProject.reports?.length && (
              <p className="text-sm text-gray-400 text-center py-4">No attachments or reports yet.</p>
            )}
          </div>
        </div>
      )}

      {/* ── IMAGE PREVIEW ─────────────────────────────────────────────────────── */}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-80 flex items-center justify-center px-4"
          onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setPreviewUrl(null)}
              className="absolute -top-10 right-0 text-white text-2xl hover:text-red-400">✕</button>
            <img src={previewUrl} alt="preview" className="w-full rounded-xl shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjectManager;